import { isFeatureShown } from "components/account_management/usage/featureUsageConfig";
import { graphql } from "lib/generated/app-service-gql";
import { FeatureEntityType } from "lib/types/graphQLEnums";
import { useGraphQlQuery } from "../useGraphQlClient";
import { UsageFilters, usageWindow } from "./usageWindow";

const companyFeatureUsageQuery = graphql(`
  query companyFeatureUsage($request: CompanyFeatureUsageRequest!) {
    companyFeatureUsage(request: $request) {
      features {
        entityType
        daysUsed
      }
    }
  }
`);

/**
 * Every feature's use was recorded from this UTC day. Before it only notice views exist, and only
 * the first and last day each user saw each notice, so earlier days read low.
 */
export const FEATURE_USAGE_COMPLETE_FROM = "2026-10-01";

export type FeatureUsage = {
  entityType: FeatureEntityType;
  /** Days each user used it, added up across users. */
  daysUsed: number;
  /** Its fraction of the days used across every feature, 0 when nothing was used. */
  share: number;
};

export type CompanyFeatureUsage = {
  /** Every feature the page lists, most used first, those nobody used included with zero. */
  features: FeatureUsage[];
  /** Days used, added up across every feature. */
  totalDaysUsed: number;
  /** Whether the window reaches back before every feature's use was recorded. */
  startsBeforeCompleteData: boolean;
  isLoading: boolean;
  isError: boolean;
};

/**
 * How many days the company's users used each feature in the last `days` UTC days, today included,
 * with notice use as one feature or split by the notice's stage.
 *
 * The server leaves out features nobody used; the legend and bars list every one, so they're
 * filled in here. Keyed on the UTC day, as the App activity chart is, so a cached window never
 * outlives it.
 */
export function useCompanyFeatureUsage(
  filters: UsageFilters,
  splitNoticesByStage: boolean,
): CompanyFeatureUsage {
  const { today, startsBefore } = usageWindow(filters.days);
  // Left out when off, as the team and payment type are when unset.
  const request = { ...filters, splitNoticesByStage: splitNoticesByStage || undefined };
  const { data, isLoading, isError } = useGraphQlQuery(
    ["companyFeatureUsage", request, today],
    companyFeatureUsageQuery,
    [{ request }],
  );

  const daysUsedByType = new Map(
    (data?.companyFeatureUsage.features ?? []).map((feature) => [
      feature.entityType,
      feature.daysUsed,
    ]),
  );
  const totalDaysUsed = [...daysUsedByType.values()].reduce((sum, daysUsed) => sum + daysUsed, 0);

  // A stable sort, so features used equally keep the enum's alphabetical order.
  const features = Object.values(FeatureEntityType)
    .filter((entityType) => isFeatureShown(entityType, splitNoticesByStage))
    .map((entityType) => {
      const daysUsed = daysUsedByType.get(entityType) ?? 0;
      return { entityType, daysUsed, share: totalDaysUsed ? daysUsed / totalDaysUsed : 0 };
    })
    .sort((a, b) => b.daysUsed - a.daysUsed);

  return {
    features,
    totalDaysUsed,
    startsBeforeCompleteData: startsBefore(FEATURE_USAGE_COMPLETE_FROM),
    isLoading,
    isError,
  };
}
